import { useState, useEffect, useRef } from 'react';
import { message } from 'antd';
import { ResourceApi, type ResourceTreeNode } from '../api';

// 内部使用的本体节点类型
export interface OntologyNode {
  key: string;
  title: string;
  type: 'folder' | 'ontology';
  children?: OntologyNode[];
  description?: string;
  createTime?: string;
  author?: string;
  version?: string;
  tags?: string[];
  isEditing?: boolean;
  id?: number;
  ontologyId?: number;
  parentId?: number;
  canView?: boolean;
  canEdit?: boolean;
  canManage?: boolean;
}

export const useResourceTree = () => {
  const [ontologyList, setOntologyList] = useState<OntologyNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const hasMounted = useRef(false);

  // 转换后端数据为前端格式
  const convertResourceTreeToOntologyNodes = (resourceNodes: ResourceTreeNode[]): OntologyNode[] => {
    return resourceNodes.map(node => ({
      key: `${node.type.toLowerCase()}-${node.id}`,
      title: node.name,
      type: node.type === 'DIRECTORY' ? 'folder' : 'ontology',
      id: node.id,
      canView: node.canView ?? true,
      canEdit: node.canEdit ?? false,
      canManage: node.canManage ?? false,
      ...(node.type === 'ONTOLOGY' && {
        ontologyId: (node as any).ontologyId || node.id,
      }),
      children: node.children && node.children.length > 0
        ? convertResourceTreeToOntologyNodes(node.children)
        : undefined,
      ...(node.type === 'ONTOLOGY' && {
        description: '本体描述信息',
        createTime: new Date().toISOString().split('T')[0],
        author: '系统管理员',
        version: '1.0.0',
        tags: ['本体'],
      })
    }));
  };

  // 创建默认目录
  const createDefaultDirectory = async () => {
    try {
      console.log('🚀 创建默认目录: Default');
      const result = await ResourceApi.createDirectory('Default');
      console.log('✅ 默认目录创建成功:', result);
      message.success('默认目录创建成功');
      await loadResourceTree();
    } catch (error: any) {
      console.error('❌ 创建默认目录失败:', error);
      message.error(`创建默认目录失败：${error.message || '未知错误'}`);
      setOntologyList([
        {
          key: 'default-local',
          title: 'Default',
          type: 'folder',
          children: [],
        },
      ]);
    }
  };

  // 加载资源树数据
  const loadResourceTree = async () => {
    if (isLoading) {
      console.log('⚠️ 资源树正在加载中，跳过重复请求');
      return;
    }

    try {
      setIsLoading(true);
      setLoading(true);
      console.log('🚀 加载资源树数据...');

      const resourceData = await ResourceApi.getResourceTree();
      console.log('📨 资源树响应:', resourceData);

      if (resourceData && Array.isArray(resourceData)) {
        if (resourceData.length === 0) {
          console.log('📁 资源树为空，创建 Default 目录');
          await createDefaultDirectory();
          return;
        }

        const convertedData = convertResourceTreeToOntologyNodes(resourceData);
        setOntologyList(convertedData);
        console.log('✅ 资源树加载成功，节点数量:', convertedData.length);
      } else {
        console.warn('⚠️ 资源树数据格式不正确');
        setOntologyList([]);
      }
    } catch (error: any) {
      console.error('❌ 加载资源树失败:', error);
      message.error(`加载资源树失败：${error.message || '未知错误'}`);
      setOntologyList([
        {
          key: 'default',
          title: 'Default',
          type: 'folder',
          children: [],
        },
      ]);
    } finally {
      setLoading(false);
      setIsLoading(false);
    }
  };

  // 递归检查是否有本体类型的节点
  const hasOntologies = (nodes: OntologyNode[]): boolean => {
    for (const node of nodes) {
      if (node.type === 'ontology') {
        return true;
      }
      if (node.children && hasOntologies(node.children)) {
        return true;
      }
    }
    return false;
  };

  // 获取所有目录选项
  const getDirectoryOptions = (nodes: OntologyNode[], excludeKey?: string): { label: string; value: number }[] => {
    const options: { label: string; value: number }[] = [
      { label: '根目录', value: 0 }
    ];

    const traverse = (nodeList: OntologyNode[], prefix = '') => {
      nodeList.forEach(node => {
        if (node.type === 'folder' && node.key !== excludeKey && node.id) {
          options.push({
            label: `${prefix}${node.title}`,
            value: node.id
          });

          if (node.children) {
            traverse(node.children, `${prefix}${node.title}/`);
          }
        }
      });
    };

    traverse(nodes);
    return options;
  };

  useEffect(() => {
    if (hasMounted.current) {
      return;
    }
    hasMounted.current = true;
    loadResourceTree();
  }, []);

  return {
    ontologyList,
    setOntologyList,
    loading,
    loadResourceTree,
    hasOntologies,
    getDirectoryOptions,
  };
};